import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Documents } from '../../../types/documents.response';
import { CommonModule, DatePipe } from '@angular/common';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { FileUploadService } from '../../../services/file-upload.service';
import { MessageService } from 'primeng/api';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { DocumentType } from '../../../types/document-type.enum';
import { DocumentsViewComponent } from '../documents-view/documents-view.component';
import { DocumentService } from '../../../services/document.service';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { DynamicDialogRef, DynamicDialogConfig, DialogService } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { PanelModule } from 'primeng/panel';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';

@Component({
  selector: 'app-documents-dialog',
  standalone: true,
  providers: [MessageService, DialogService],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DatePipe,
    PdfViewerModule,
    DragDropModule,
    ButtonModule,
    CardModule,
    PanelModule,
    ToastModule,
    TableModule,
    ProgressSpinnerModule,
    InputTextModule,
    DropdownModule,
  ],
  template: `
    <p-toast></p-toast>
    <p-card>
      <ng-template pTemplate="header">
        <div class="card-header">
          <h2><i class="pi pi-file"></i> Documentos - {{ config.data.personName }}</h2>
        </div>
      </ng-template>

      <div class="documents-container">
        <!-- Informações -->
        <p-panel header="Informações" [toggleable]="false" styleClass="compact-panel">
          <p><strong>{{ config.data.personName }}</strong></p>
          <p>
            @if(documentList.length){ 
              Existem {{ documentList.length }} documento(s) cadastrado(s)
            } @else{ 
              Não existem documentos guardados!
            }
          </p>
        </p-panel>

        <!-- Lista de Documentos -->
        @if(documentList.length){
        <p-panel [header]="'Lista de Documentos (' + documentList.length + ')'" [toggleable]="false" styleClass="compact-panel">
          <div class="table-container">
            <p-table [value]="documentList" [tableStyle]="{'min-width': '100%'}" styleClass="p-datatable-sm">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width: 20%">Tipo</th>
                  <th style="width: 15%">Número</th>
                  <th style="width: 15%">Origem</th>
                  <th style="width: 25%">Nome do Arquivo</th>
                  <th style="width: 15%">Data</th>
                  <th style="width: 10%; text-align: center">Ações</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-doc>
                <tr>
                  <td class="doc-type">{{ doc.documentType }}</td>
                  <td class="doc-number">{{ doc.number }}</td>
                  <td class="doc-origin">{{ doc.origin }}</td>
                  <td class="doc-filename">{{ doc.fileName }}</td>
                  <td class="doc-date">{{ doc.createdAt | date : 'dd/MM/yyyy HH:mm' }}</td>
                  <td style="text-align: center">
                    <p-button
                      icon="pi pi-eye"
                      styleClass="action-button p-button-text p-button-rounded p-button-sm"
                      (onClick)="openDocumentView(doc.id, doc.fileName)"
                      pTooltip="Visualizar">
                    </p-button>
                    <p-button
                      icon="pi pi-trash"
                      styleClass="delete-button p-button-text p-button-rounded p-button-sm p-button-danger"
                      (onClick)="deleteDocumentId(doc.id)"
                      pTooltip="Excluir">
                    </p-button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </div>
        </p-panel>
        }

        <!-- Upload de Documentos -->
        <p-panel header="Adicionar Novo Documento" [toggleable]="false" styleClass="compact-panel">
          <div *ngIf="isUploading" class="spinner-overlay">
            <p-progressSpinner styleClass="custom-spinner" strokeWidth="4"></p-progressSpinner>
            <p class="message-text">Enviando documento...</p>
          </div>
          
          <form [formGroup]="docForm" (submit)="onDocSubmit()" class="document-form">
            <div class="form-grid">
              <div class="form-row">
                <div class="form-field">
                  <label for="documentType" class="field-label">Tipo de Documento*</label>
                  <p-dropdown
                    id="documentType"
                    formControlName="documentType"
                    [options]="documentTypes"
                    optionLabel="description"
                    optionValue="value"
                    placeholder="Selecione o tipo"
                    styleClass="compact-dropdown"
                    [ngClass]="{ 'p-invalid': isFieldInvalid('documentType') }">
                  </p-dropdown>
                  <small *ngIf="isFieldInvalid('documentType')" class="p-error">
                    Informar o tipo de documento é obrigatório
                  </small>
                </div>

                <div class="form-field">
                  <label for="number" class="field-label">Número*</label>
                  <input
                    #number
                    id="number"
                    pInputText
                    formControlName="number"
                    class="compact-input"
                    placeholder="Número do documento"
                    maxlength="20"
                    [ngClass]="{ 'p-invalid': isFieldInvalid('number') }">
                  <small *ngIf="isFieldInvalid('number')" class="p-error">
                    Informar o número do documento é obrigatório
                  </small>
                </div>

                <div class="form-field">
                  <label for="origin" class="field-label">Origem*</label>
                  <input
                    #origin
                    id="origin"
                    pInputText
                    formControlName="origin"
                    class="compact-input"
                    placeholder="Origem do documento"
                    maxlength="20"
                    [ngClass]="{ 'p-invalid': isFieldInvalid('origin') }">
                  <small *ngIf="isFieldInvalid('origin')" class="p-error">
                    Informar a origem do documento é obrigatório
                  </small>
                </div>
              </div>

              <!-- File Input e Drop Zone -->
              <div class="upload-section">
                <input
                  type="file"
                  id="fileToUpload"
                  #fileInput
                  (change)="onFileSelected($event)"
                  style="display: none;">
                
                <div
                  id="drop-zone"
                  class="upload-drop-zone"
                  (drop)="onDrop($event)"
                  (dragover)="onDragOver($event)"
                  (dragleave)="onDragLeave($event)"
                  (click)="fileInput.click()">
                  <i class="pi pi-cloud-upload" style="font-size: 2rem; color: #6c757d;"></i>
                  <p>Arraste & Solte Arquivos Aqui ou Clique para Selecionar</p>
                </div>
              </div>

              <!-- Preview do Documento -->
              @if(uploadedImageUrl){
              <div class="preview-section">
                <h4>Prévia do Documento:</h4>
                <div class="preview-container">
                  @if(uploadedFileExtension !== 'pdf'){
                    <img
                      [src]="uploadedImageUrl"
                      alt="Documento Selecionado"
                      class="uploaded-image">
                  } @else {
                    <pdf-viewer
                      [src]="uploadedImageUrl"
                      [render-text]="true"
                      class="pdf-preview">
                    </pdf-viewer>
                  }
                </div>
              </div>
              }

              <!-- Botões de Ação -->
              <div class="button-row">
                <p-button
                  label="Salvar Documento"
                  icon="pi pi-save"
                  type="submit"
                  styleClass="save-button"
                  [loading]="isUploading">
                </p-button>
                <p-button
                  label="Concluir"
                  icon="pi pi-check"
                  type="button"
                  (onClick)="closeAndNavigate()"
                  styleClass="close-button-action p-button-secondary">
                </p-button>
              </div>
            </div>
          </form>
        </p-panel>
      </div>
    </p-card>
  `,
  styles: [
    `
      /* Card Header */
      .card-header {
        padding: 16px 20px;
        margin: 0;
        background: rgba(255, 230, 203, 0.1);
        border-bottom: 1px solid #FFE6CB;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .card-header h2 {
        color: #325565;
        font-weight: 600;
        font-size: 1.5rem;
        margin: 0;
        font-family: "Montserrat", sans-serif;
      }

      /* Container Principal */
      .documents-container {
        padding: 10px;
      }

      /* Panels Compactos */
      :host ::ng-deep .compact-panel {
        margin-bottom: 1rem;
      }

      :host ::ng-deep .compact-panel .p-panel-header {
        background: rgba(255, 230, 203, 0.1);
        border: 1px solid #FFE6CB;
        color: #325565;
        font-weight: 600;
        padding: 0.75rem 1rem;
      }

      :host ::ng-deep .compact-panel .p-panel-content {
        padding: 1rem;
        border: 1px solid #FFE6CB;
        border-top: none;
      }

      /* Grid de Formulário */
      .form-grid {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .form-row {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 1rem;
        align-items: end;
      }

      .form-field {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }

      .field-label {
        font-weight: 600;
        color: #325565;
        font-size: 0.875rem;
        margin-bottom: 0.25rem;
      }

      /* Inputs Compactos */
      :host ::ng-deep .compact-input {
        height: 2.5rem !important;
        font-size: 0.875rem;
        width: 100%;
      }

      :host ::ng-deep .compact-dropdown {
        height: 2.5rem !important;
        font-size: 0.875rem;
        width: 100% !important;
      }

      :host ::ng-deep .compact-dropdown .p-dropdown {
        height: 2.5rem !important;
        width: 100% !important;
      }

      /* Upload Section */
      .upload-section {
        margin-top: 1rem;
      }

      .upload-drop-zone {
        margin-top: 10px;
        padding: 2rem;
        height: 150px;
        border: 2px dashed #FFE6CB;
        border-radius: 8px;
        text-align: center;
        color: #6c757d;
        background-color: #fafafa;
        transition: all 0.3s ease;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      }

      .upload-drop-zone:hover {
        background-color: rgba(255, 230, 203, 0.2);
        border-color: #F15A22;
      }

      .upload-drop-zone.dragover {
        background-color: rgba(241, 90, 34, 0.1);
        border-color: #F15A22;
      }

      .upload-drop-zone p {
        margin: 0.5rem 0 0 0;
        font-size: 0.875rem;
      }

      /* Preview Section */
      .preview-section {
        margin-top: 1rem;
      }

      .preview-section h4 {
        color: #325565;
        font-weight: 600;
        margin-bottom: 0.5rem;
      }

      .preview-container {
        display: flex;
        justify-content: center;
        align-items: center;
        max-height: 400px;
        overflow: auto;
        border: 1px solid #FFE6CB;
        border-radius: 8px;
        background-color: #f8f9fa;
        padding: 1rem;
      }

      .uploaded-image {
        display: block;
        max-width: 100%;
        max-height: 350px;
        object-fit: contain;
        border-radius: 4px;
      }

      .pdf-preview {
        display: block;
        width: 100%;
        height: 350px;
      }

      /* Botões de Ação */
      .button-row {
        display: flex;
        gap: 0.75rem;
        margin-top: 1rem;
        justify-content: flex-start;
      }

      :host ::ng-deep .save-button {
        background-color: #F15A22 !important;
        border-color: #F15A22 !important;
        color: white !important;
      }

      :host ::ng-deep .save-button:hover {
        background-color: #E55A2B !important;
        border-color: #E55A2B !important;
      }

      :host ::ng-deep .close-button {
        color: white !important;
      }

      :host ::ng-deep .close-button-action {
        background-color: #6c757d !important;
        border-color: #6c757d !important;
      }

      :host ::ng-deep .close-button-action:hover {
        background-color: #5a6268 !important;
        border-color: #545b62 !important;
      }

      /* Loading State */
      .spinner-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(255, 255, 255, 0.9);
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        z-index: 1000;
      }

      :host ::ng-deep .custom-spinner .p-progress-spinner-circle {
        stroke: #F15A22;
      }

      .message-text {
        color: #325565;
        font-size: 0.875rem;
        font-weight: 500;
        margin-top: 1rem;
      }

      /* Table Container */
      .table-container {
        display: block;
      }

      /* PrimeNG Table Customization */
      :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
        background: white;
        color: #325565;
        font-weight: 700;
        padding: 0.75rem;
        border-bottom: 2px solid #FFE6CB;
      }

      :host ::ng-deep .p-datatable .p-datatable-tbody > tr {
        transition: background-color 0.2s;
      }

      :host ::ng-deep .p-datatable .p-datatable-tbody > tr:hover {
        background-color: #f8f9fa;
      }

      :host ::ng-deep .p-datatable .p-datatable-tbody > tr > td {
        padding: 0.75rem;
      }

      .doc-type,
      .doc-number,
      .doc-origin,
      .doc-filename,
      .doc-date {
        color: #495057;
        font-size: 0.875rem;
      }

      .doc-filename {
        word-break: break-word;
      }

      /* Action Buttons */
      :host ::ng-deep .action-button {
        color: #F15A22 !important;
      }

      :host ::ng-deep .action-button:hover {
        background-color: rgba(241, 90, 34, 0.1) !important;
      }

      :host ::ng-deep .action-button:focus,
      :host ::ng-deep .action-button:focus-visible {
        outline: none !important;
        box-shadow: none !important;
      }

      :host ::ng-deep .delete-button:hover {
        background-color: rgba(220, 53, 69, 0.1) !important;
      }

      :host ::ng-deep .delete-button:focus,
      :host ::ng-deep .delete-button:focus-visible {
        outline: none !important;
        box-shadow: none !important;
      }

      /* Validation */
      :host ::ng-deep .p-invalid {
        border-color: #dc3545 !important;
        background-color: rgba(220, 53, 69, 0.1) !important;
      }

      :host ::ng-deep .p-error {
        color: #dc3545;
        font-size: 0.75rem;
        margin-top: 0.25rem;
      }

      /* PrimeNG Card Customization */
      :host ::ng-deep .p-card {
        border: none;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      :host ::ng-deep .p-card .p-card-body {
        padding: 0;
      }

      :host ::ng-deep .p-card .p-card-content {
        padding: 0;
      }

      /* Responsive Design */
      @media (max-width: 768px) {
        .form-row {
          grid-template-columns: 1fr;
          gap: 0.75rem;
        }

        .card-header h2 {
          font-size: 1.25rem;
        }

        .button-row {
          flex-direction: column;
        }

        .button-row p-button {
          width: 100%;
        }

        .upload-drop-zone {
          height: 120px;
          padding: 1rem;
        }

        .preview-container {
          max-height: 300px;
        }

        .uploaded-image,
        .pdf-preview {
          max-height: 250px;
        }
      }
    `,
  ],
})
export class DocumentsDialogComponent implements OnInit, OnChanges {
  docForm: FormGroup;
  documentList: Documents[] = [];
  items = Array.from({ length: this.documentList.length }, (_, i) => i + 1);
  uploadedImageUrl: string | null = null;
  selectedFile!: File | null;
  person: any = null;
  files: File[] = [];
  uploadedFileUrl: string | null = null;
  uploadedFileExtension: string | undefined = '';

  documentType: string = '';
  number: string = '';
  origin: string = '';

  peopleId: number;
  personName: string = '';

  isUploading: boolean = false;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private fileUploadService: FileUploadService,
    private documentService: DocumentService,
    private dialogService: DialogService,
    private messageService: MessageService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {
    this.documentList = this.config.data.documents;
    this.person = this.config.data.person;
    this.peopleId = this.config.data.peopleId;
    this.personName = this.config.data.personName;

    this.docForm = this.fb.group({
      documentType: ['', Validators.required],
      number: ['', [Validators.required]],
      origin: ['', Validators.required], 
    });

    this.documentTypes.sort((a, b) =>
      a.description.localeCompare(b.description)
    );
  }
  @ViewChildren('documentType') documentTypeInputField!: ElementRef;
  @ViewChild('number') numberInputField!: ElementRef;
  @ViewChild('origin') originInputField!: ElementRef;
  @ViewChild('fileInput') fileInput!: ElementRef;

  private readonly DOCUMENT_TYPE_ORDER = [
    'FORM',
    'RG',
    'CPF',
    'CNH',
    'CERTIDAO_DE_NASCIMENTO',
    'CERTIDAO_DE_CASAMENTO',
    'CTPS',
    'CONTA_DE_LUZ',
    'CONTA_DE_AGUA',
    'CONTA_DE_TELEFONE',
    'EXTRATO_PLANO_SAUDE',
    'EXTRATO_CARTAO_DE_CREDITO',
    'EXTRATO_DE_IMPOSTO_DE_RENDA',
    'COMPROVANTE_DE_ISENCAO_DE_IPTU',
    'COMPROVANTE_DE_PAGAMENTO_DE_IPTU',
    'CONTRA_CHEQUES',
    'EXTRATO_DE_BENEFICIO_SOCIAL',
    'LAUDO_MEDICO',
    'BOLETIM_DE_OCORRENCIA',
  ];

  ngOnInit(): void {
    // Carrega documentos da API para garantir dados atualizados
    this.loadDocuments();
  }

  private sortDocuments(documents: Documents[]): Documents[] {
    return [...documents].sort((a, b) => {
      // 1. Ordenação por data (ignorando hora/minuto/segundo)
      const getDateTimestamp = (dateString: string) => {
        const date = new Date(dateString);
        // Define hora, minuto, segundo e milissegundo como zero
        date.setHours(0, 0, 0, 0);
        return date.getTime();
      };

      const timestampA = getDateTimestamp(a.createdAt);
      const timestampB = getDateTimestamp(b.createdAt);

      // Data mais recente primeiro
      const dateComparison = timestampB - timestampA;
      if (dateComparison !== 0) return dateComparison;

      // 2. Ordenação por tipo de documento
      const indexA = this.DOCUMENT_TYPE_ORDER.indexOf(a.documentType);
      const indexB = this.DOCUMENT_TYPE_ORDER.indexOf(b.documentType);

      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      return a.documentType.localeCompare(b.documentType);
    });
  }
  /*
  ngOnChanges(changes: SimpleChanges): void {}
  */
  ngOnChanges(changes: SimpleChanges): void {
    // Verifica se a propriedade 'config.data' foi alterada
    if (changes['config'] && this.config?.data?.documents) {
      this.documentList = this.sortDocuments(this.config.data.documents);
    }
  }

  showUploadedImageById(peopleId: number, documentId: number) {
    this.fileUploadService.downloadFileById(peopleId, documentId).subscribe({
      next: (fileBlob: Blob) => {
        // Create a URL for the image blob and set it to be displayed
        this.uploadedImageUrl = URL.createObjectURL(fileBlob);
        /*console.log('url gerada para a imagem: ', this.uploadedImageUrl);*/
      },
      error: (error) => {
        /*console.error('Error fetching image:', error);*/
        alert('Erro ao carregar a imagem. Tente novamente.');
      },
    });
  }

  documentTypes = Object.keys(DocumentType).map((key) => ({
    value: key,
    description: DocumentType[key as keyof typeof DocumentType],
  }));

  onFileSelected(event: any) {
    this.selectedFile = event.target.files[0];
    if (this.selectedFile) {
      this.uploadedFileExtension = this.selectedFile.name
        .split('.')
        .pop()
        ?.toLowerCase();
      this.handleFileUpload(this.selectedFile);
    }
    this.cdr.detectChanges();
  }

  onDragOver(event: DragEvent) {
    // Previne o comportamento padrão para permitir o drop
    event.preventDefault();
    event.stopPropagation();
    // Opcional: Adicionar uma classe para mudar a aparência quando o item está sendo arrastado
  }

  onDragLeave(event: DragEvent) {
    // Opcional: Remover a classe de arrasto
  }

  // Função chamada quando o evento 'drop' é disparado
  onDrop(event: DragEvent) {
    event.preventDefault(); // Previne o comportamento padrão
    event.stopPropagation(); // Previne que o evento se propague

    // Captura os arquivos arrastados
    const files = event.dataTransfer?.files;

    if (files && files.length > 0) {
      const file = files[0]; // Aqui, vamos pegar o primeiro arquivo (caso haja mais de um)
      /*console.log('Arquivo recebido:', file);*/
      this.selectedFile = file; // Assign the dropped file to selectedFile
      this.uploadedFileExtension = file.name.split('.').pop()?.toLowerCase();
      this.handleFileUpload(file);
    } else {
      /*console.log('Nenhum arquivo foi enviado.');*/
    }
  }

  // Função que processa o upload do arquivo
  handleFileUpload(file: File) {
    this.uploadedFileExtension = file.name.split('.').pop()?.toLowerCase();
    const reader = new FileReader();

    reader.onload = () => {
      this.uploadedImageUrl = reader.result as string; // Para imagens, você pode criar uma URL de dados
      // Se for um arquivo PDF ou outro tipo de arquivo, você pode tratar de forma diferente
    };

    // Verifique o tipo de arquivo
    // Verifique o tipo de arquivo
    if (file.type.startsWith('image/')) {
      reader.readAsDataURL(file); // Usado para ler imagens
    } else if (file.type === 'application/pdf') {
      // Para PDF, você pode exibir um visualizador ou algo relacionado
      this.uploadedImageUrl = URL.createObjectURL(file); // Para exibir um PDF diretamente
    } else {
      alert('Tipo de arquivo não suportado!');
      this.uploadedImageUrl = null;
      this.uploadedFileExtension = '';
      this.selectedFile = null;
    }
  }

  // Method to start the upload process
  startUpload(): void {
    /*console.log(this.files);*/
    // Logic to handle file uploads can go here
  }

  onDocSubmit() {
  // 1. Valida o formulário primeiro
  if (this.docForm.invalid) {
    Object.keys(this.docForm.controls).forEach(key => {
      this.docForm.get(key)?.markAsTouched();
    });
    this.messageService.add({
      severity: 'warn',
      summary: 'Atenção',
      detail: 'Por favor, preencha todos os campos obrigatórios!',
      life: 3000
    });
    return;
  }

  // 2. Valida o arquivo
  if (!this.selectedFile) {
    this.messageService.add({
      severity: 'warn',
      summary: 'Atenção',
      detail: 'Favor escolher um arquivo!',
      life: 3000
    });
    return;
  }

  this.isUploading = true;

  const documentType = this.docForm.get('documentType')?.value;
  const number = this.docForm.get('number')?.value;
  const origin = this.docForm.get('origin')?.value;
  const peopleId = this.peopleId;

  // 3. Envia o arquivo
  this.fileUploadService
    .uploadFile(this.selectedFile, peopleId, documentType, number, origin)
    .subscribe({
      next: (response: any) => {
        this.isUploading = false;
        
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Documento enviado com sucesso!',
          life: 3000
        });

        // Atualiza a lista
        this.loadDocuments();

        // Limpa o formulário
        this.cleanDocForm();
        if (this.fileInput?.nativeElement) {
          this.fileInput.nativeElement.value = '';
        }
        this.selectedFile = null;
        this.uploadedImageUrl = null;
        
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.isUploading = false;
        console.error('Erro ao enviar documento:', error);
        
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: error?.error?.message || 'Erro ao enviar documento. Tente novamente.',
          life: 5000
        });
      },
    });
}
  cleanDocForm() {
    this.docForm.reset(); // Reseta todos os campos do formulário
    this.selectedFile = null; // Limpa o arquivo selecionado
    this.uploadedImageUrl = null; // Limpa a URL da imagem carregada
    this.uploadedFileExtension = ''; // Limpa a extensão do arquivo

  // Opcional: redefinir para valores padrão se necessário
  // this.docForm.patchValue({
  //   documentType: '',
  //   number: '',
  //   origin: ''
  // });
  }

  showUploadedImage(fileName: string) {
    this.fileUploadService.downloadFile(fileName).subscribe({
      next: (fileBlob: Blob) => {
        // Create a URL for the image blob and set it to be displayed
        this.uploadedImageUrl = URL.createObjectURL(fileBlob);
      },
      error: (error) => {
        /*console.error('Error fetching image:', error);*/
        // alert('Erro ao carregar a imagem. Tente novamente.');
      },
    });
  }

  openDocumentView(id: number, fileName: string) {
    const dialogRef = this.dialogService.open(DocumentsViewComponent, {
      width: '90vw',
      height: '87vh',
      maximizable: true,
      header: 'Visualização de Documento',
      data: { id, fileName },
    });
    if (dialogRef) {
      dialogRef.onClose.subscribe((result) => {
        if (result) {
          this.messageService.add({
            severity: 'info',
            summary: 'Informação',
            detail: 'Documento visualizado',
            life: 3000
          });
        }
      });
    }
  }

  closeAndNavigate(): void {
    this.ref.close({
      saved: true,
      person: this.config.data.person,
    });
  }

 deleteDocumentId(documentId: number) {
  const userWantsToDeleteDocument = confirm('Deseja excluir este documento?');
  if (!userWantsToDeleteDocument) {
    return;
  }

  if (!documentId || isNaN(documentId)) {
    console.error('ID do documento inválido:', documentId);
    this.messageService.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'ID do documento inválido.',
      life: 3000
    });
    return;
  }

  this.documentService.deleteDocumentId(documentId).subscribe({
    next: (response) => {
      // ✅ Remove da lista localmente
      this.documentList = this.documentList.filter(
        (doc) => doc.id !== documentId
      );
      
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: response?.message || 'Documento excluído com sucesso.',
        life: 3000
      });
      
      this.cdr.detectChanges();
    },
    error: (err: Error) => {
      console.error('Erro ao excluir documento:', err);
      
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: err.message || 'Erro ao excluir documento. Tente novamente.',
        life: 3000
      });
    },
  });
}
  loadDocuments() {
    this.documentService.getAllDocumentsByPeopleId(this.peopleId).subscribe({
      next: (data) => {
        this.documentList = this.sortDocuments(data);
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Erro ao carregar documentos:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao carregar documentos. Tente novamente.',
          life: 3000
        });
      },
    });
  }

  moveToNextField(currentFieldId: string, nextFieldId: string): void {
    const control = this.docForm.get(currentFieldId);

    if (control) {
      // Marca o campo como tocado para acionar a validação
      control.markAsTouched();
      control.updateValueAndValidity(); // Atualiza a validade após marcar como tocado
    }

    if (this.isFieldInvalid(currentFieldId)) {
      // Se o campo ainda estiver inválido, mantém o foco e exibe o erro
      const currentField = document.getElementById(
        currentFieldId
      ) as HTMLInputElement;
      currentField.focus();
    } else {
      // Se o campo for válido, foca no próximo campo
      const nextField = document.getElementById(nextFieldId) as HTMLElement;
      nextField?.focus();
    }
  }

  isFieldInvalid(field: string): boolean {
    const control = this.docForm.get(field);
    return !!(control?.invalid && (control.touched || control.dirty));
  }
}
